import { Injectable, computed, effect, signal } from '@angular/core'
import { RetailUser } from '../services/retail-auth.types'

const TOKEN_KEY = 'us_retail_token'

// Session holder for the retail experience. Owns the bearer token (persisted to
// localStorage `us_retail_token` so a signed-in session survives reloads) and the
// resolved current user. `authed` is derived from the token. The route guard and
// shell read authed(); the auth flow calls setSession() after verify/register and
// the interceptor calls clearSession() on a 401.
@Injectable({ providedIn: 'root' })
export class RetailAuthService {
  private _token = signal<string | null>(this.read())
  readonly token = this._token.asReadonly()
  readonly authed = computed(() => !!this._token())

  readonly currentUser = signal<RetailUser | null>(null)

  constructor() {
    effect(() => {
      const value = this._token()
      if (typeof localStorage === 'undefined') return
      try {
        if (value) localStorage.setItem(TOKEN_KEY, value)
        else localStorage.removeItem(TOKEN_KEY)
      } catch {
        // storage unavailable (private mode / quota) - keep in-memory state only
      }
    })
  }

  setSession(token: string, user: RetailUser): void {
    this._token.set(token)
    this.currentUser.set(user)
  }

  clearSession(): void {
    this._token.set(null)
    this.currentUser.set(null)
  }

  logout(): void {
    this.clearSession()
  }

  private read(): string | null {
    if (typeof localStorage === 'undefined') return null
    try {
      return localStorage.getItem(TOKEN_KEY)
    } catch {
      return null
    }
  }
}
